import { useState, useEffect } from 'react';
import { tagsMapFor } from '../../../data/tagAssignments';
import API from '../../../data/api';
import { downloadExcel } from '../../../utils/excel';
import { t } from '../../../company/labels';

/** Loads the Store tags and which tags each store has (for the "Etiquetas" column filter + export). */
export function useStoreTags(stores) {
    const [availableTags, setAvailableTags] = useState([]);
    const [storeTagsMap, setStoreTagsMap] = useState({});

    useEffect(() => {
        const fetchTags = async () => {
            try {
                const api = API.Instance();
                const response = await api.requestTags({ modelType: 'Store' });
                const tags = response.tags || [];
                setAvailableTags(tags);

                // One request for every row's tags (data/tagAssignments.js)
                const tagsMap = await tagsMapFor(stores, 'Store', tags);
                setStoreTagsMap(tagsMap);
            } catch (error) {
                console.error('Error fetching tags:', error);
            }
        };
        if (stores && stores.length > 0) {
            fetchTags();
        }
    }, [stores]);

    return { availableTags, storeTagsMap };
}

/** Column filters + sorter state of the stores table (controlled, so the export can mirror it). */
export function useStoreTableState() {
    const [columnFilters, setColumnFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});

    const onTableChange = (pagination, filters, sorter) => {
        setColumnFilters(filters || {});
        setSortedInfo(Array.isArray(sorter) ? sorter[0] || {} : sorter || {});
    };

    return { columnFilters, sortedInfo, onTableChange };
}

/**
 * `rows` already reflects the search/canal/cliente filters from TableActions.
 * Apply the column filters (and active sorter) on top so the export matches what's on screen.
 */
export function filterAndSortRows(rows, columns, columnFilters, sortedInfo) {
    const filtered = (rows || []).filter((record) =>
        columns.every((col) => {
            const values = columnFilters[col.key];
            if (!values || !values.length || !col.onFilter) return true;
            return values.some((value) => col.onFilter(value, record));
        })
    );

    const sortedColumn = columns.find((col) => col.key === sortedInfo.columnKey && col.sorter);
    if (sortedColumn && sortedInfo.order) {
        const direction = sortedInfo.order === 'descend' ? -1 : 1;
        return [...filtered].sort((a, b) => sortedColumn.sorter(a, b) * direction);
    }
    return filtered;
}

/** Exports exactly the rows the table is showing. */
export function exportStoresToExcel(visibleRows, storeTagsMap) {
    const promotersColumn = t('promotores');
    const titles = ['Piso de Venta', 'RIF', 'Cliente', 'Canal', 'Tipo de Piso', promotersColumn, 'Latitud', 'Longitud', 'Estado', 'Etiquetas'];
    const data = visibleRows.map((store) => ({
        'Piso de Venta': store.name || '',
        RIF: store.rif || '',
        Cliente: store.clientName || '',
        Canal: store.channel || '',
        'Tipo de Piso': store.storeType || '',
        [promotersColumn]: store.promotorNames || '',
        Latitud: store.coordinates?.latitude ?? '',
        Longitud: store.coordinates?.longitude ?? '',
        Estado: store.active || '',
        Etiquetas: (storeTagsMap[store._id] || []).map((tag) => tag.name).join(', ')
    }));

    downloadExcel(titles, data, 'Pisos de Venta.xlsx');
}
